import React, { useRef } from "react";

import MenuIcon from "@mui/icons-material/Menu";
import { Box, Stack } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Menu } from "components/popups";
import Highcharts from "highcharts";
import HighchartsReact, { HighchartsReactRefObject } from "highcharts-react-official";
import exportData from "highcharts/modules/export-data";
import exporting from "highcharts/modules/exporting";
import fullscreen from "highcharts/modules/full-screen";
import offlineExporting from "highcharts/modules/offline-exporting";
import { IDispersionBucket } from "store/types";

offlineExporting(Highcharts);
exporting(Highcharts);
exportData(Highcharts);
fullscreen(Highcharts);

interface DispersionChartProps {
  data: IDispersionBucket[] | null;
  loading: boolean;
}

const BAR_COLOR = "#4690F5";
const FONT_FAMILY = "Roboto, Helvetica, Arial, sans-serif";

// Soft vertical gradient for the columns — brighter at the top, fading downward.
const BAR_GRADIENT: Highcharts.GradientColorObject = {
  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
  stops: [
    [0, "#5B9DF7"],
    [1, "#9FC7FA"]
  ]
};

const BAR_GRADIENT_HOVER: Highcharts.GradientColorObject = {
  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
  stops: [
    [0, "#3D85F2"],
    [1, "#7FB4F8"]
  ]
};

export const DispersionChart: React.FC<DispersionChartProps> = ({ data, loading }) => {
  const chartRef = useRef<HighchartsReactRefObject>(null);

  const categories = data?.map((b) => b.label) ?? [];
  const counts = data?.map((b) => b.count) ?? [];

  const handleExport = (type: string) => {
    (chartRef.current?.chart as any)?.exportChartLocal({ type });
  };

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 285,
      borderRadius: 16,
      marginTop: 56,
      spacingBottom: 16,
      backgroundColor: "transparent",
      style: { fontFamily: FONT_FAMILY }
    },
    title: {
      text: "Return Dispersion",
      align: "left",
      style: {
        fontWeight: "500",
        fontSize: "18px",
        fontFamily: FONT_FAMILY,
        color: "#071F35"
      }
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } },
    xAxis: {
      categories,
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        style: {
          color: "#496682",
          fontSize: "12px",
          fontFamily: FONT_FAMILY
        }
      }
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: "#EEF2F8",
      gridLineDashStyle: "Dash",
      lineWidth: 0,
      tickWidth: 0,
      allowDecimals: false,
      labels: {
        style: {
          color: "#92A7BC",
          fontSize: "12px",
          fontFamily: FONT_FAMILY
        }
      }
    },
    tooltip: {
      useHTML: true,
      outside: true,
      backgroundColor: "transparent",
      borderWidth: 0,
      shadow: false,
      padding: 0,
      formatter: function () {
        const card = (inner: string) =>
          `<div style="font-family:${FONT_FAMILY};background:#fff;border:1px solid #EEF2F8;border-radius:12px;box-shadow:0 8px 24px rgba(18,18,23,0.12);width:240px;box-sizing:border-box;padding:12px 14px;">${inner}</div>`;

        const index = this.point.index;
        const bucket = data?.[index];
        if (!bucket) {
          return card(
            `<div style="font-weight:500;font-size:13px;color:#071F35;">${this.x}</div><div style="font-size:12px;color:#496682;margin-top:2px;">Count: ${this.y}</div>`
          );
        }

        const maxVisible = 10;
        const holdingsToShow = bucket.holdings.slice(0, maxVisible);
        const remaining = bucket.holdings.length - maxVisible;

        const holdingsList = holdingsToShow
          .map(
            (h) =>
              `<div style="padding:3px 0;font-size:12px;color:#071F35;display:flex;align-items:center;justify-content:space-between;gap:8px;"><span style="max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block;">${h.name}</span> <span style="color:#496682;white-space:nowrap;">${h.ratio.toFixed(2)}x</span></div>`
          )
          .join("");

        const moreText =
          remaining > 0 ? `<div style="padding:4px 0 0;font-size:11px;color:#92A7BC;">+${remaining} more</div>` : "";

        return card(
          `<div style="font-weight:600;font-size:13px;color:#071F35;margin-bottom:2px;">${bucket.label}</div>
          <div style="font-size:12px;color:#496682;margin-bottom:8px;">${bucket.count} holding${bucket.count !== 1 ? "s" : ""}</div>
          ${holdingsList}${moreText}`
        );
      }
    },
    legend: { enabled: false },
    plotOptions: {
      column: {
        borderRadius: 6,
        borderWidth: 0,
        color: BAR_GRADIENT,
        maxPointWidth: 52,
        pointPadding: 0.08,
        groupPadding: 0.18,
        states: {
          hover: { color: BAR_GRADIENT_HOVER, brightness: 0 }
        },
        dataLabels: {
          enabled: true,
          crop: false,
          overflow: "allow",
          y: -2,
          style: {
            fontSize: "11px",
            fontWeight: "500",
            fontFamily: FONT_FAMILY,
            color: "#92A7BC",
            textOutline: "none"
          }
        }
      }
    },
    series: [
      {
        name: "Holdings",
        type: "column",
        data: counts
      }
    ],
    colors: [BAR_COLOR]
  };

  return (
    <Box
      className="chart"
      sx={{
        flexGrow: 1,
        position: "relative",
        borderRadius: "8px",
        backgroundColor: "#ffffff",
        boxShadow: "0 1px 2px 0 #1212171a",
        "&:hover .chart-option": {
          opacity: "1 !important"
        }
      }}
    >
      <AsyncContentMui isLoading={loading} boxProps={{ sx: { height: 285 } }}>
        <HighchartsReact highcharts={Highcharts} ref={chartRef} options={options} />
        <Stack
          className="menu-trigger-blocks"
          sx={{
            position: "absolute",
            top: "10px",
            right: "10px",
            flexDirection: "row",
            gap: 1
          }}
        >
          <Menu
            iconButtonProps={{ className: "chart-option", sx: { opacity: 0, transition: "opacity 0.3s linear" } }}
            keepTriggerEnabled
            icon={<MenuIcon />}
            options={[
              {
                title: "View in full screen",
                onClick: () => (chartRef.current?.chart as any).fullscreen.toggle()
              },
              {
                title: "Print chart",
                onClick: () => (chartRef.current?.chart as any).print(),
                hasLine: true
              },
              {
                title: "Download PNG image",
                onClick: () => handleExport("image/png")
              },
              {
                title: "Download JPEG image",
                onClick: () => handleExport("image/jpeg")
              },
              {
                title: "Download PDF Document",
                onClick: () => handleExport("application/pdf")
              },
              {
                title: "Download SVG vector image",
                onClick: () => handleExport("image/svg+xml"),
                hasLine: true
              },
              {
                title: "Download CSV",
                onClick: () => (chartRef.current?.chart as any).downloadCSV()
              },
              {
                title: "Download XLS",
                onClick: () => (chartRef.current?.chart as any).downloadXLS()
              }
            ]}
          />
        </Stack>
      </AsyncContentMui>
    </Box>
  );
};
